import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { 
  ChartBarIcon, 
  UsersIcon, 
  ChatAlt2Icon, 
  EyeIcon,
  ArrowRightIcon
} from '@heroicons/react/outline';
import { 
  collection, 
  getDocs, 
  query, 
  where 
} from 'firebase/firestore';
import { db } from 'config/firebase';
import { seedVillageData } from 'utils/seeder';

export default function CMSDashboard() {
  const { slug } = useParams();
  const [stats, setStats] = useState([
    { name: 'Total Berita', stat: '0', icon: ChartBarIcon, color: 'text-blue-600', bg: 'bg-blue-100' },
    { name: 'Kegiatan Terdaftar', stat: '0', icon: UsersIcon, color: 'text-green-600', bg: 'bg-green-100' },
    { name: 'Laporan Masuk', stat: '0', icon: ChatAlt2Icon, color: 'text-purple-600', bg: 'bg-purple-100' },
    { name: 'Total Perangkat', stat: '0', icon: EyeIcon, color: 'text-orange-600', bg: 'bg-orange-100' },
  ]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const villageQ = query(collection(db, "villages"), where("slug", "==", slug));
        const villageSnap = await getDocs(villageQ);
        if (villageSnap.empty) return;
        const villageId = villageSnap.docs[0].id;

        const newsSnap = await getDocs(collection(db, `villages/${villageId}/news`));
        const activitiesSnap = await getDocs(collection(db, `villages/${villageId}/activities`));
        const reportsSnap = await getDocs(collection(db, `villages/${villageId}/reports`));
        const staffSnap = await getDocs(collection(db, `villages/${villageId}/staff`));

        setStats([
          { name: 'Total Berita', stat: newsSnap.size.toString(), icon: ChartBarIcon, color: 'text-blue-600', bg: 'bg-blue-100' },
          { name: 'Kegiatan Terdaftar', stat: activitiesSnap.size.toString(), icon: UsersIcon, color: 'text-green-600', bg: 'bg-green-100' },
          { name: 'Laporan Masuk', stat: reportsSnap.size.toString(), icon: ChatAlt2Icon, color: 'text-purple-600', bg: 'bg-purple-100' },
          { name: 'Total Perangkat', stat: staffSnap.size.toString(), icon: EyeIcon, color: 'text-orange-600', bg: 'bg-orange-100' },
        ]);
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    };

    fetchStats();
  }, [slug]);
  return (
    <div className="space-y-8 animate-fadeIn">
      <div>
        <h2 className="text-2xl font-extrabold text-gray-900 tracking-tight">Selamat Datang, Admin!</h2>
        <p className="mt-1 text-sm text-gray-500">Berikut adalah ringkasan perkembangan website desa Anda hari ini.</p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((item) => (
          <div key={item.name} className="relative overflow-hidden bg-white px-6 py-6 shadow-sm border border-gray-100 rounded-3xl transition-all duration-300 hover:shadow-md hover:-translate-y-1">
            <dt>
              <div className={`absolute p-3 rounded-2xl ${item.bg} ${item.color}`}>
                <item.icon className="h-6 w-6" aria-hidden="true" />
              </div>
              <p className="ml-16 truncate text-sm font-bold text-gray-500">{item.name}</p>
            </dt>
            <dd className="ml-16 flex items-baseline">
              <p className="text-2xl font-extrabold text-gray-900">{item.stat}</p>
            </dd>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Recent Activity */}
        <div className="bg-white shadow-sm border border-gray-100 rounded-3xl overflow-hidden px-8 py-8">
           <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-extrabold text-gray-800">Aktivitas Terakhir</h3>
              <button className="text-primary text-sm font-bold flex items-center hover:underline">
                Lihat Semua <ArrowRightIcon className="w-4 h-4 ml-1" />
              </button>
           </div>
           <div className="flow-root">
            <ul className="-mb-8">
              {[1, 2, 3].map((item, itemIdx) => (
                <li key={itemIdx}>
                  <div className="relative pb-8">
                    {itemIdx !== 2 ? (
                      <span className="absolute left-5 top-5 -ml-px h-full w-0.5 bg-gray-100" aria-hidden="true" />
                    ) : null}
                    <div className="relative flex items-start space-x-4">
                      <div className="relative px-1">
                        <div className="h-8 w-8 bg-gray-50 border border-gray-100 rounded-full flex items-center justify-center ring-8 ring-white">
                           <div className="h-2 w-2 bg-primary rounded-full"></div>
                        </div>
                      </div>
                      <div className="min-w-0 flex-1 py-1.5 mt-[-4px]">
                        <div className="text-sm text-gray-500">
                          <span className="font-bold text-gray-800">Admin</span> memposting berita baru{' '}
                          <a href="#" className="font-semibold text-primary">
                            Pembangunan Jalan Desa Selesai
                          </a>
                        </div>
                        <div className="mt-1 text-xs text-gray-400">
                          2 jam yang lalu
                        </div>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white shadow-sm border border-gray-100 rounded-3xl overflow-hidden px-8 py-8">
          <h3 className="text-lg font-extrabold text-gray-800 mb-6">Aksi Cepat</h3>
          <div className="grid grid-cols-2 gap-4">
                <button className="flex flex-col items-center justify-center p-6 bg-blue-50/50 border border-blue-100 rounded-2xl group transition-all duration-300 hover:bg-blue-100/50">
                   <div className="p-3 bg-white shadow-sm rounded-xl mb-3 text-blue-600 group-hover:scale-110 transition-transform">
                      <ChartBarIcon className="w-6 h-6" />
                   </div>
                   <span className="text-sm font-bold text-blue-700">Tulis Berita</span>
                </button>
                <button className="flex flex-col items-center justify-center p-6 bg-green-50/50 border border-green-100 rounded-2xl group transition-all duration-300 hover:bg-green-100/50">
                   <div className="p-3 bg-white shadow-sm rounded-xl mb-3 text-green-600 group-hover:scale-110 transition-transform">
                      <UsersIcon className="w-6 h-6" />
                   </div>
                   <span className="text-sm font-bold text-green-700">Tambah Kegiatan</span>
                </button>
                <button className="flex flex-col items-center justify-center p-6 bg-purple-50/50 border border-purple-100 rounded-2xl group transition-all duration-300 hover:bg-purple-100/50">
                   <div className="p-3 bg-white shadow-sm rounded-xl mb-3 text-purple-600 group-hover:scale-110 transition-transform">
                      <ChatAlt2Icon className="w-6 h-6" />
                   </div>
                   <span className="text-sm font-bold text-purple-700">Kelola Layanan</span>
                </button>
                <button className="flex flex-col items-center justify-center p-6 bg-orange-50/50 border border-orange-100 rounded-2xl group transition-all duration-300 hover:bg-orange-100/50">
                   <div className="p-3 bg-white shadow-sm rounded-xl mb-3 text-orange-600 group-hover:scale-110 transition-transform">
                      <EyeIcon className="w-6 h-6" />
                   </div>
                   <span className="text-sm font-bold text-orange-700">Statistik Web</span>
                </button>
           </div>
        </div>

        {/* System & Initialization */}
        <div className="bg-white shadow-sm border border-gray-100 rounded-3xl overflow-hidden px-8 py-8 transition-all hover:shadow-md">
           <h3 className="text-lg font-extrabold text-gray-800 mb-4">Sistem & Inisialisasi</h3>
           <p className="text-sm text-gray-500 mb-6 leading-relaxed">
             Gunakan fitur ini jika database Firebase Anda masih kosong. Ini akan membuat dokumen desa "glg-panah" dan struktur data dasar secara otomatis.
           </p>
           <button 
             onClick={seedVillageData}
             className="w-full py-4 bg-primary text-white font-extrabold rounded-2xl shadow-lg shadow-primary/20 hover:bg-primary-dark transition-all transform hover:scale-[1.02] active:scale-95 flex items-center justify-center space-x-2"
           >
             <ChartBarIcon className="w-5 h-5" />
             <span>Inisialisasi Data Firebase</span>
           </button>
           <div className="mt-4 flex items-center justify-center space-x-2 text-xs text-gray-400">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <p>Firebase Connected: {slug}</p>
           </div>
        </div>
      </div>
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
